import React, { useState } from 'react';
import Icon from '@/components/ui/Icon';
import cn from '@/utils/cn';
import { tintStyle } from './tint';

// AIDEV-NOTE: The dev pages' shared left-rail shell (DevDocs, Content Audit, Agent Runs) — frame,
// collapse strip, pinned header, scrollable body. Pages own only their content; keeping the frame
// here is what keeps the three rails from drifting apart again.

const frameClass = cn(
  'tw-box-border tw-relative tw-flex-shrink-0 tw-border-0 tw-border-r tw-border-solid',
  'tw-border-spa-border tw-bg-spa-bg-secondary tw-flex tw-flex-col tw-rounded-tr-lg',
);

const toggleButtonClass = cn(
  'tw-flex tw-items-center tw-justify-center tw-border-0 tw-bg-transparent',
  'tw-cursor-pointer tw-text-spa-secondary hover:tw-text-spa-on-bg tw-rounded-md hover:tw-bg-spa-bg-tertiary',
);

interface SPARailProps {
  // Washes the brand strip with the entity's color, e.g. the program a drill is inside.
  accentColor?: string | null;
  bodyClassName?: string;
  children: React.ReactNode;
  className?: string;
  collapseLabel?: string;
  collapsed?: boolean;
  expandLabel?: string;
  footer?: React.ReactNode;
  header: React.ReactNode;
  headerClassName?: string;
  onCollapsedChange?: (collapsed: boolean) => void;
  subheader?: React.ReactNode;
}

// Collapse is uncontrolled by default; pass collapsed + onCollapsedChange to drive it (e.g. the
// Agent Runs rail auto-collapses on selection at phone widths).
const SPARail = ({
  accentColor,
  bodyClassName,
  children,
  className,
  collapseLabel = 'Collapse sidebar',
  collapsed: controlledCollapsed,
  expandLabel = 'Expand sidebar',
  footer,
  header,
  headerClassName,
  onCollapsedChange,
  subheader,
}: SPARailProps) => {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const collapsed = controlledCollapsed ?? internalCollapsed;

  const setCollapsed = (value: boolean) => {
    setInternalCollapsed(value);
    onCollapsedChange?.(value);
  };

  if (collapsed) {
    return (
      <aside className={cn(frameClass, 'tw-w-10 tw-items-center tw-pt-3', className)}>
        <button
          aria-label={expandLabel}
          className={cn(toggleButtonClass, 'tw-p-2')}
          type="button"
          onClick={() => setCollapsed(false)}
        >
          <Icon icon="ChevronRight" size={16} />
        </button>
      </aside>
    );
  }

  return (
    <aside className={cn(frameClass, 'tw-w-80 tw-overflow-hidden', className)}>
      <div
        className={cn(
          'tw-box-border tw-flex tw-flex-shrink-0 tw-items-start tw-gap-2 tw-p-3 tw-pt-4',
          headerClassName,
        )}
        style={accentColor ? tintStyle(accentColor) : undefined}
      >
        <div className="tw-min-w-0 tw-flex-1">{header}</div>
        <button
          aria-label={collapseLabel}
          className={cn(toggleButtonClass, 'tw-flex-shrink-0 tw-p-1.5')}
          type="button"
          onClick={() => setCollapsed(true)}
        >
          <Icon icon="ChevronLeft" size={16} />
        </button>
      </div>

      {subheader && (
        <div className="tw-box-border tw-flex-shrink-0 tw-px-3 tw-pb-2.5 tw-pt-3">{subheader}</div>
      )}

      <div className={cn('dev-docs-scrollable tw-min-h-0 tw-flex-1 tw-overflow-y-auto', bodyClassName)}>
        {children}
      </div>

      {footer && (
        <div
          className={cn(
            'tw-box-border tw-flex-shrink-0 tw-border-0 tw-border-t tw-border-solid',
            'tw-border-spa-border tw-p-2',
          )}
        >
          {footer}
        </div>
      )}
    </aside>
  );
};

export default SPARail;
